import { useState, useEffect, useCallback } from "react";
import { useScrollReveal } from "./useScrollReveal";

/**
 * Compteur animé de 0 jusqu'à la valeur cible quand la section devient visible.
 * Les séparateurs sont déduits de la chaîne cible : « 9,7 % », « 9.7% », « 35 000 €+ », « €35,000+ ».
 */
export const useCountUp = (targetString, { duration = 2000, delay = 0 } = {}) => {
  const { ref, isRevealed } = useScrollReveal({ threshold: 0.3 });
  const [displayValue, setDisplayValue] = useState(targetString);

  const animate = useCallback(() => {
    const numMatch = targetString.match(/\d[\d\s\u00A0.,]*/);
    if (!numMatch) return;
    const rawNum = numMatch[0].trim().replace(/[.,\s\u00A0]+$/, "");
    const prefix = targetString.slice(0, targetString.indexOf(rawNum));
    const suffix = targetString.slice(targetString.indexOf(rawNum) + rawNum.length);

    // Séparateur décimal : un point ou une virgule suivi d'un ou deux chiffres en fin de nombre
    const dec = rawNum.match(/([.,])(\d{1,2})$/);
    const decSep = dec ? dec[1] : null;
    const decPlaces = dec ? dec[2].length : 0;

    // Séparateur de milliers : celui placé devant un groupe de trois chiffres
    const thou = rawNum.match(/\d([\s\u00A0.,])\d{3}/);
    const thouSep = thou && thou[1] !== decSep ? thou[1] : "";

    let cleaned = rawNum.replace(/[\s\u00A0]/g, "");
    if (decSep) {
      cleaned = cleaned.split(decSep === "," ? "." : ",").join("").replace(decSep, ".");
    } else {
      cleaned = cleaned.replace(/[.,]/g, "");
    }
    const target = parseFloat(cleaned);
    if (Number.isNaN(target)) return;

    const group = (intStr) => (thouSep ? intStr.replace(/\B(?=(\d{3})+(?!\d))/g, thouSep) : intStr);

    const startTime = performance.now();
    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      if (progress >= 1) {
        setDisplayValue(targetString);
        return;
      }
      const current = target * (1 - Math.pow(1 - progress, 3));
      let formatted;
      if (decPlaces > 0) {
        const [i, d] = current.toFixed(decPlaces).split(".");
        formatted = group(i) + decSep + d;
      } else {
        formatted = group(String(Math.round(current)));
      }
      setDisplayValue(prefix + formatted + suffix);
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [targetString, duration]);

  useEffect(() => {
    if (!isRevealed) return;
    if (delay > 0) {
      const timer = setTimeout(animate, delay);
      return () => clearTimeout(timer);
    }
    animate();
  }, [isRevealed, animate, delay]);

  return { ref, displayValue, isRevealed };
};
